import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, ShoppingBag, LayoutDashboard, ArrowRight, Utensils,
         Store, Building2, ChartBar, Wallet, ChefHat } from "lucide-react";
import { Logo, PoweredBy } from "../components/Brand";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";

const TABLES = ["SN108", "LB103", "NN105", "PP102"];

const FEATURES = [
  { icon: QrCode, title: "Scan · Order · Pay Once", desc: "One QR unlocks the entire food court. Order from many restaurants, one payment." },
  { icon: ChefHat, title: "Kitchen Display System", desc: "Real-time KDS with SLA tracking, bump actions and delay alerts per outlet." },
  { icon: ChartBar, title: "Command Centre Analytics", desc: "Corporate → Mall → Food Court → Group → Restaurant → Item drill-down." },
  { icon: Wallet, title: "Billing & Settlements", desc: "Master receipts, per-restaurant tax invoices, payment allocation & settlement ledgers." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { setTable } = useCart();

  const startDemo = async (code) => {
    try {
      const { data } = await api.get(`/public/table/${code}`);
      setTable(data);
      navigate("/order");
    } catch { navigate(`/t/${code}`); }
  };

  return (
    <div className="min-h-screen bg-[#08183A] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #D4AF37 0, transparent 35%), radial-gradient(circle at 85% 30%, #F59E0B 0, transparent 30%)" }} />
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-10 py-5 max-w-7xl mx-auto">
        <Logo />
        <button onClick={() => navigate("/login")}
          className="glass rounded-full px-5 py-2 text-sm font-semibold flex items-center gap-2 hover:border-amber-500 transition-colors"
          data-testid="header-login-btn">
          <LayoutDashboard size={16} /> Admin Login
        </button>
      </header>

      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-10 pt-8 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block text-xs tracking-[0.22em] text-amber-400/90 font-semibold mb-4">
              ENTERPRISE FOOD COURT COMMERCE · PROPOSED FOR OMAXE MALLS
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              One QR for the <span className="gold-text">entire</span> food court.
            </h1>
            <p className="text-slate-400 mt-5 text-base sm:text-lg max-w-xl leading-relaxed">
              Scan your table, browse every restaurant, add dishes from multiple brands into one cart, pay once,
              and track each kitchen live. A complete first-party commerce & intelligence layer for modern malls.
            </p>
          </motion.div>

          <div className="mt-8 card-surface rounded-2xl p-5">
            <div className="flex items-center gap-2 text-sm text-slate-300 mb-3">
              <QrCode size={18} className="text-amber-400" />
              <span className="font-semibold">Try the customer experience — pick a demo table</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {TABLES.map((t) => (
                <button key={t} onClick={() => startDemo(t)}
                  className="gold-btn rounded-xl px-4 py-2.5 text-sm flex items-center gap-2"
                  data-testid={`demo-table-${t}`}>
                  <Utensils size={14} /> Table {t} <ArrowRight size={14} />
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-3">Omaxe Chowk · Dawatpur Food Court, Chandni Chowk</p>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
          className="relative">
          <img src="https://images.pexels.com/photos/34884133/pexels-photo-34884133.jpeg?auto=compress&cs=tinysrgb&w=1200"
            alt="Luxury food court" className="rounded-3xl w-full object-cover max-h-[80vh] shadow-2xl border border-amber-500/20" />
          <div className="absolute -bottom-5 -left-3 sm:left-6 glass rounded-2xl p-4 w-56">
            <div className="text-[10px] text-amber-400 tracking-widest">MASTER ORDER · LIVE</div>
            <div className="font-display font-bold text-lg mt-1">₹1,265 · 3 restaurants</div>
            <div className="flex gap-1.5 mt-2">
              <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">READY</span>
              <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">PREPARING</span>
              <span className="text-[10px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">ACCEPTED</span>
            </div>
          </div>
        </motion.div>
      </section>

      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-10 pb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="card-surface rounded-2xl p-6 hover:border-amber-500/40 transition-colors">
              <div className="h-11 w-11 rounded-xl gold-gradient flex items-center justify-center mb-4">
                <f.icon className="text-slate-950" size={22} />
              </div>
              <h3 className="font-display font-bold text-lg">{f.title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          {[["Store", Store, "Restaurant & Group Consoles"], ["Building", Building2, "Mall & Corporate Dashboards"], ["Bag", ShoppingBag, "Customer QR Ordering"]].map(([k, Icon, label]) => (
            <div key={k} className="flex items-center gap-3 glass rounded-xl px-5 py-4">
              <Icon className="text-amber-400" size={20} />
              <span className="text-sm text-slate-300 font-medium">{label}</span>
            </div>
          ))}
        </div>
      </section>

      <PoweredBy />
    </div>
  );
}
